import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, CircularProgress, InputAdornment, TextField, Chip, Checkbox } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

const getFullName = (opt) => [opt?.name, opt?.last_name].filter(Boolean).join(" ");

const AsyncUserSelect = ({
  value = [],
  onChange,
  searchUsers,
  minChars = 2,
  pageSize = 10,
  label = "Usuarios",
  placeholder = "Buscar por email o documento...",
  limitTags = 3,
}) => {
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef(new Map());
  const debounceTimerRef = useRef(null);
  const debounceMs = 300;

  const runSearch = useCallback(
    async (query) => {
      if (!searchUsers) return;
      setLoading(true);
      try {
        const { items = [] } = await searchUsers({ q: query, limit: pageSize });
        setOptions(items);
        cacheRef.current.set(query, { items, ts: Date.now() });
      } catch (error) {
        console.error("Error buscando usuarios:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [searchUsers, pageSize],
  );

  // Aplica debounce al texto de búsqueda
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const query = searchText.trim();

    if (query.length < minChars) {
      setOptions([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      const cached = cacheRef.current.get(query);
      if (cached && Date.now() - cached.ts < 60_000) {
        setOptions(cached.items);
        return;
      }
      runSearch(query);
    }, debounceMs);

    return () => clearTimeout(debounceTimerRef.current);
  }, [searchText, minChars, runSearch, debounceMs]);

  const passthroughFilter = useMemo(() => (opts) => opts, []);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      loading={loading}
      options={options}
      filterOptions={passthroughFilter}
      getOptionLabel={(opt) => getFullName(opt) || opt?.email || opt?.id_document || ""}
      isOptionEqualToValue={(opt, val) => !!val && opt?.id === val?.id}
      value={value}
      onChange={(_, selectedUsers) => onChange?.(selectedUsers || [])}
      inputValue={searchText}
      onInputChange={(_, nextValue) => setSearchText(nextValue)}
      noOptionsText={searchText.trim().length < minChars ? `Escriba al menos ${minChars} caracteres` : "Sin resultados"}
      loadingText="Buscando…"
      renderTags={(selected, getTagProps) =>
        selected.map((opt, index) => (
          <Chip {...getTagProps({ index })} key={opt.id} label={getFullName(opt) || opt.email || opt.id_document} size="small" />
        ))
      }
      limitTags={limitTags}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Checkbox checked={selected} size="small" />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span style={{ fontWeight: 600 }}>{getFullName(option) || "Sin nombre"}</span>
            <span style={{ fontSize: 12, opacity: 0.8 }}>{option.email || option.id_document}</span>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="terciary" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AsyncUserSelect;
